import { fetchSenateAPI, fetchSenateAPIWithLocalStorage } from "@/apis/core";
import type {
  TableFiveData,
  TableSixData,
  TableTwoData,
} from "@/components/tables/type";
import {
  defaultTableFive,
  defaultTableSix,
} from "@/components/tables/variables";
import { localStorageKey } from "@/configs/enum";
import {
  getNumberData,
  groupByType,
  mapExperience,
  mapTableFiveData,
  mapTableSixData,
} from "@/utils/helper";
import dayjs from "dayjs";
import _ from "lodash";
import { registerPath } from "../path";
import type {
  AcademicKnowledge,
  Address,
  AgeTextRR,
  ComplainB,
  Concept,
  ConsentForApi,
  ExpertiseOrExperience,
  FileAnnounce,
  FinalAffiliationB,
  FinalStageResponse,
  HistoryOfWrongdoing,
  IncompatibilityB,
  KnowledgeAndAbility,
  Occupation,
  PersonalRecord,
  PoliticalParty,
  Prefix,
  PressRelease,
  ProceedB,
  RegisterHistory,
  SumTextRR,
} from "../type";
import type {
  BusinessResponse,
  EducationResponse,
  ExperienceResponse,
  GetAddressRequest,
  GetAddressResponse,
  GetAffiliationRequest,
  GetAffiliationResponse,
  GetAumphurResponse,
  GetBusinessRequest,
  GetBusinessResponse,
  GetCandidateRequest,
  GetCandidateResponse,
  GetConceptRequest,
  GetConceptResponse,
  GetConsentResponse,
  GetConsentsBySRDIdResponse,
  GetDisciplineRequest,
  GetDisciplineResponse,
  GetEducationListResponse,
  GetEducationRequest,
  GetEducationResponse,
  GetExperienceRequest,
  GetExperienceResponse,
  GetExpertiseOrExperienceRequest,
  GetExpertiseOrExperienceResponse,
  GetFileAnnounceTypeRequest,
  GetFileAnnounceTypeResponse,
  GetIncompatibilityRequest,
  GetIncompatibilityResponse,
  GetKnowledgeAndAbilityRequest,
  GetKnowledgeAndAbilityResponse,
  GetKnowledgeRequest,
  GetKnowledgeResponse,
  GetLastStepRequest,
  GetLastStepResponse,
  GetListCandidateInfoByIdResponse,
  GetListForCommitteeResponse,
  GetListHistoryResponse,
  GetListResponse,
  GetNationalityResponse,
  GetOccupationRequest,
  GetOccupationResponse,
  GetPersonalRecordRequest,
  GetPersonalRecordResponse,
  GetPoliticalRequest,
  GetPoliticalResponse,
  GetPrefixRequest,
  GetPrefixResponse,
  GetPropertyRequest,
  GetPropertyResponse,
  GetProvinceResponse,
  GetReplacementResponse,
  GetTitleListResponse,
  GetTumbolResponse,
  GetUploadFileRequest,
  GetUploadFileResponse,
  GetUserInformationRequest,
  GetUserInformationResponse,
  GetUserProfileResponse,
  GetZipCodeResponse,
  LastStepResponse,
  PropertyResponse,
  UploadDocumentsB,
  GetFormConfigResponse,
  GetPropertiesUploadFileResponse,
  GetAgeTextRequest,
  GetAgeTextResponse,
  GetSumDateTextRequest,
  GetSumDateTextResponse,
} from "./type";

const method = "get";

async function getList(): Promise<GetListResponse> {
  const pathname = registerPath.list;
  const request: any = {
    method,
    pathname,
    debug: "getList",
    auth: false,
  };
  const response = await fetchSenateAPI(request);
  if (response.status === 200 && response.data && response.data.length > 0) {
    const data = response.data.map((el: PressRelease, index: number) => {
      return {
        index,
        id: el.id,
        title: el.title,
        description: el.description,
        organizationId: el.organizationId,
        // group: el.group,
        type: el.type?.toLowerCase(),
        startDate: el?.startDate ? dayjs(el.startDate) : undefined,
        endDate: el?.endDate ? dayjs(el.endDate) : undefined,
      };
    });
    return { ...response, data };
  }
  return response;
}

async function getTitleList(): Promise<GetTitleListResponse> {
  const pathname = registerPath.titleList;
  const response = await fetchSenateAPIWithLocalStorage({
    method,
    pathname,
    debug: "getTitleList",
    key: localStorageKey.REGISTER_PREFIX,
  });
  if (response.status === 200 && !_.isEmpty(response.data)) {
    const data = response.data.map(({ id, name }: any, i: number) => ({
      id,
      key: `title-option-${i}`,
      value: name,
      label: name,
    }));
    return { ...response, data };
  }
  return response;
}

async function getProvince(): Promise<GetProvinceResponse> {
  const pathname = registerPath.province;
  const response = await fetchSenateAPIWithLocalStorage({
    method,
    pathname,
    debug: "getProvince",
    key: localStorageKey.REGISTER_PROVINCE,
  });
  if (response.status === 200 && !_.isEmpty(response.data)) {
    const data = response.data.map(({ id, name }: any, i: number) => ({
      key: `province-option-${i}`,
      value: id,
      label: name,
    }));
    return { ...response, data };
  }
  return response;
}

async function getProvince2(): Promise<GetProvinceResponse> {
  const pathname = registerPath.province;
  const response = await fetchSenateAPIWithLocalStorage({
    method,
    pathname,
    debug: "getProvince",
    key: localStorageKey.REGISTER_PROVINCE,
  });
  if (response.status === 200 && !_.isEmpty(response.data)) {
    const data = response.data.map(({ id, name }: any, i: number) => ({
      id,
      key: `province-option-${i}`,
      value: name,
      label: name,
    }));
    return { ...response, data };
  }
  return response;
}

async function getAumphur(id: number): Promise<GetAumphurResponse> {
  const pathname = `${registerPath.aumphur}/${id}`;
  const response = await fetchSenateAPI({
    method,
    pathname,
    debug: "getAumphur",
  });
  if (response.status === 200 && !_.isEmpty(response.data)) {
    const data = response.data.map(({ id, name }: any, i: number) => ({
      key: `aumpthur-option-${i}`,
      value: id,
      label: name,
    }));
    return { ...response, data };
  }
  return response;
}

async function getAumphur2(id: number): Promise<GetAumphurResponse> {
  const pathname = `${registerPath.aumphur}/${id}`;
  const response = await fetchSenateAPI({
    method,
    pathname,
    debug: "getAumphur",
  });
  if (response.status === 200 && !_.isEmpty(response.data)) {
    const data = response.data.map(({ id, name }: any, i: number) => ({
      id,
      key: `aumpthur-option-${i}`,
      value: name,
      label: name,
    }));
    return { ...response, data };
  }
  return response;
}

async function getTumbol(id: number): Promise<GetTumbolResponse> {
  const pathname = `${registerPath.tumbol}/${id}`;
  const response = await fetchSenateAPI({
    method,
    pathname,
    debug: "getTumbol",
  });
  if (response.status === 200 && !_.isEmpty(response.data)) {
    const data = response.data.map(({ id, name }: any, i: number) => ({
      key: `tumbol-option-${i}`,
      value: id,
      label: name,
    }));
    return { ...response, data };
  }
  return response;
}

async function getTumbol2(id: number): Promise<GetTumbolResponse> {
  const pathname = `${registerPath.tumbol}/${id}`;
  const response = await fetchSenateAPI({
    method,
    pathname,
    debug: "getTumbol",
  });
  if (response.status === 200 && !_.isEmpty(response.data)) {
    const data = response.data.map(({ id, name }: any, i: number) => ({
      id,
      key: `tumbol-option-${i}`,
      value: name,
      label: name,
    }));
    return { ...response, data };
  }
  return response;
}

async function getZipCode(id: number): Promise<GetZipCodeResponse> {
  const pathname = `${registerPath.zipcode}/${id}`;
  const response = await fetchSenateAPI({
    method,
    pathname,
    debug: "getZipCode",
  });
  if (response.status === 200 && !_.isEmpty(response.data)) {
    const data = response.data.map(({ id, zipcode }: any) => ({
      id,
      zipcode,
    }))[0];
    return { ...response, data };
  }
  return response;
}

async function getConsent(
  request: GetPersonalRecordRequest,
): Promise<GetConsentResponse> {
  const pathname = registerPath.consent;
  const response = await fetchSenateAPI({
    method,
    pathname,
    debug: "getPersonalRecord",
    request,
  });
  if (response.status === 200 && !_.isEmpty(response.data)) {
    const rawData = response.data;
    const etc2 =
      rawData?.registerTypeEtc2 === null
        ? undefined
        : rawData?.registerTypeEtc2;
    const data: ConsentForApi = {
      id: rawData?.id,
      status: rawData?.status,
      lastStep: rawData?.lastStep || 0,
      personList: rawData?.personList || [],

      successDate: rawData?.successDate,
      registerType: rawData?.registerType,
      registerTypeEtc: rawData?.registerTypeEtc || "",
      registerTypeEtc2: etc2,

      title: rawData?.title,
      prefix: rawData?.prefix,
      firstName: rawData?.firstName || "",
      lastName: rawData?.lastName || "",
      identityCard: rawData?.identityCard || "",

      checkBoxList: rawData?.checkBoxList || [],

      text1: rawData?.text1 || "",
      text2: rawData?.text2 || "",
      text3: rawData?.text3 || "",
      text4: rawData?.text4 || "",
      text5: rawData?.text5 || "",

      experiences: rawData?.experiences || [],
    };
    return { ...response, data };
  }
  return response;
}

async function getPersonalRecord(
  request: GetPersonalRecordRequest,
): Promise<GetPersonalRecordResponse> {
  const pathname = registerPath.personalRecord;
  const response = await fetchSenateAPI({
    method,
    pathname,
    debug: "getPersonalRecord",
    request,
  });
  if (response.status === 200 && !_.isEmpty(response.data)) {
    const rawData = response.data;
    const childList = rawData?.childList || [];
    const children = childList.map((item: any) => ({
      id: item?.id,
      title: item?.title,
      prefix: item?.childPrefix,
      firstName: item?.childFirstName,
      lastName: item?.childLastName,
      age: item?.childAge,
    }));
    const data: PersonalRecord = {
      status: rawData?.status,
      lastStep: rawData?.lastStep || 0,
      updatedDate: rawData?.updatedDate
        ? dayjs(rawData?.updatedDate)
        : undefined,
      candidate: {
        title: rawData?.title,
        prefix: rawData?.prefix,
        firstName: rawData?.firstName || "",
        lastName: rawData?.lastName || "",
        identityCard: rawData?.identityCard || "",
        birthday: rawData?.birthDate ? dayjs(rawData?.birthDate) : undefined,
        submitDate: rawData?.submitDate ? dayjs(rawData?.submitDate) : undefined,
        nationality: rawData?.nationality,
      },
      checkBox: rawData?.oldNameStatus === 1 ? true : false,
      deleteOldNameList: [],
      oldNameList: rawData?.oldNameList || [],
      father: {
        title: rawData?.fTitle,
        prefix: rawData?.fPrefix,
        firstName: rawData?.fFirstName || "",
        lastName: rawData?.fLastName || "",
        nationality: rawData?.fNationality,
        career: rawData?.fOccupation || "",
      },
      mother: {
        title: rawData?.mTitle,
        prefix: rawData?.mPrefix,
        firstName: rawData?.mFirstName || "",
        lastName: rawData?.mLastName || "",
        nationality: rawData?.mNationality,
        career: rawData?.mOccupation || "",
      },
      maritalStatus: rawData?.maritalStatus || 0,
      spouse: {
        title: rawData?.maritalTitle,
        prefix: rawData?.maritalPrefix,
        firstName: rawData?.maritalFirstName || "",
        lastName: rawData?.maritalLastName || "",
        nationality: rawData?.maritalNationality,
        birthday: rawData?.maritalBirthDate
          ? dayjs(rawData.maritalBirthDate)
          : undefined,
        career: rawData?.maritalOccupation || "",
        workplace: rawData?.maritalWorkPlace || "",
      },
      fatherOfSpouse: {
        title: rawData?.mfTitle,
        prefix: rawData?.mfPrefix,
        firstName: rawData?.mfFirstName || "",
        lastName: rawData?.mfLastName || "",
        nationality: rawData?.mfNationality,
        career: rawData?.mfOccupation || "",
      },
      motherOfSpouse: {
        title: rawData?.mmTitle,
        prefix: rawData?.mmPrefix,
        firstName: rawData?.mmFirstName || "",
        lastName: rawData?.mmLastName || "",
        nationality: rawData?.mmNationality,
        career: rawData?.mmOccupation || "",
      },
      deleteChildren: [],
      children,
      ageNotLessThan: rawData?.ageNotLessThan || 0,
      ageNotMoreThan: rawData?.ageNotMoreThan || 0,
      textAgeApplicant: rawData?.textAgeApplicant || "",
      textNationalityApplicant: rawData?.textNationalityApplicant || "",
      nationality_name: rawData?.nationality_name || "",
    };
    return { ...response, data };
  }
  return response;
}

async function getAddress(
  request: GetAddressRequest,
): Promise<GetAddressResponse> {
  const pathname = registerPath.address;
  const response = await fetchSenateAPI({
    method,
    pathname,
    debug: "getAddress",
    request,
  });
  if (response.status === 200 && !_.isEmpty(response.data)) {
    const rawData = response.data;
    const address = rawData?.address || {};
    const data: Address = {
      status: rawData?.status,
      lastStep: rawData?.lastStep || 0,
      aAddress: {
        aNo: address?.aNo || "",
        aBuilding: address?.aBuilding || "",
        aGroup: address?.aGroup || "",
        aAlley: address?.aAlley || "",
        aRoad: address?.aRoad || "",
        aProvinceId: address?.aProvinceId,
        aAmphurId: address?.aAmphurId,
        aDistrictId: address?.aDistrictId,
        aZipCodeId: address?.aZipCodeId,
        aPhone: address?.aPhone || "",
        aMobile: address?.aMobile || "",
        aEmail: address?.aEmail || "",
        cType: address?.cType || "S",
      },
      cAddress: {
        cWorkPlace: address?.cWorkPlace || "",
        cNo: address?.cNo || "",
        cBuilding: address?.cBuilding || "",
        cGroup: address?.cGroup || "",
        cAlley: address?.cAlley || "",
        cRoad: address?.cRoad || "",
        cProvinceId: address?.cProvinceId,
        cAmphurId: address?.cAmphurId,
        cDistrictId: address?.cDistrictId,
        cZipCodeId: address?.cZipCodeId,
        cPhone: address?.cPhone || "",
        cMobile: address?.cMobile || "",
        cEmail: address?.cEmail || "",
      },
    };
    return { ...response, data };
  }
  return response;
}

async function getEducation(
  request: GetEducationRequest,
): Promise<GetEducationResponse> {
  const pathname = registerPath.education;
  const response = await fetchSenateAPI({
    method,
    pathname,
    debug: "getEducation",
    request,
  });
  if (response.status === 200 && !_.isEmpty(response.data)) {
    const rawData = response.data;
    const educations = rawData?.educations || [];
    const trainings = rawData?.trainings || [];
    const data: EducationResponse = {
      status: rawData?.status,
      lastStep: rawData?.lastStep || 0,
      educations: educations.map((item: any) => ({
        id: item?.id,
        eduId: item?.eduId,
        eduLevel: item?.eduLevel || "",
        eduName: item?.eduName || "",
        eduMajor: item?.eduMajor || "",
        eduPlace: item?.eduPlace || "",
        eduYear: item?.eduYear || "",
        status: item?.status,
      })),
      trainings: trainings.map((item: any) => ({
        id: item?.id,
        tName: item?.tName || "",
        tInstitute: item?.tInstitute || "",
        tStartDate: item?.tStartDate ? dayjs(item.tStartDate) : undefined,
        tEndDate: item?.tEndDate ? dayjs(item.tEndDate) : undefined,
        status: item?.status,
      })),
    };
    return { ...response, data };
  }
  return response;
}

async function getExperience(
  request: GetExperienceRequest,
): Promise<GetExperienceResponse> {
  const pathname = registerPath.experience;
  const response = await fetchSenateAPI({
    method,
    pathname,
    debug: "getExperience",
    request,
  });
  if (response.status === 200 && !_.isEmpty(response.data)) {
    const rawData = response.data;
    const experiences = rawData?.experiences || [];
    const data: ExperienceResponse = {
      status: rawData?.status,
      lastStep: rawData?.lastStep || 0,
      workExperiences: experiences?.length
        ? mapExperience(
            experiences.filter((item: any) => item?.expType === "W"),
          )
        : [],
      mentorExperiences: experiences?.length
        ? mapExperience(
            experiences.filter((item: any) => item?.expType === "M"),
          )
        : [],
    };
    return { ...response, data };
  }
  return response;
}

async function getProperty(
  request: GetPropertyRequest,
): Promise<GetPropertyResponse> {
  const pathname = registerPath.property;
  const response = await fetchSenateAPI({
    method,
    pathname,
    debug: "getProperty",
    request,
  });
  if (response.status === 200 && !_.isEmpty(response.data)) {
    const rawData = response.data;
    const properties = !_.isEmpty(rawData?.properties)
      ? rawData?.properties.reduce(
          (
            acc: any,
            { id, experienceType, title, startDate, endDate }: any,
          ) => {
            const key = `table${experienceType}`;
            const item: TableTwoData = {
              id,
              jobPosition: title || "",
              startDate: startDate ? dayjs(startDate) : undefined,
              endDate: endDate ? dayjs(endDate) : undefined,
            };
            if (!acc[key]) {
              acc[key] = [item];
            } else {
              acc[key].push(item);
            }
            return acc;
          },
          {},
        )
      : null;
    const data: PropertyResponse = {
      status: rawData?.status,
      lastStep: rawData?.lastStep || 0,
      activeProperties: rawData?.activeProperties || [],
      properties,
    };
    return { ...response, data };
  }
  return response;
}

async function getOccupation(
  request: GetOccupationRequest,
): Promise<GetOccupationResponse> {
  const pathname = registerPath.occupation;
  const response = await fetchSenateAPI({
    method,
    pathname,
    debug: "getOccupation",
    request,
  });
  if (response.status === 200 && !_.isEmpty(response.data)) {
    const rawData = response.data;
    const occupation = rawData?.occupation || {};
    const data: Occupation = {
      status: rawData?.status,
      lastStep: rawData?.lastStep || 0,

      checkBox1: !!occupation?.position1,
      checkBox2: !!occupation?.position2,
      checkBox3: !!occupation?.position3,
      checkBox4: !!occupation?.firstName4,
      checkBox5: !!occupation?.type5,
      checkBox6: !!occupation?.other6,

      career1: {
        occType1: occupation?.occType1 || 0,
        position1: occupation?.position1,
        level1: occupation?.level1,
        salary1: occupation?.salary1,
        org1: occupation?.org1,
        leaveYear1: occupation?.leaveYear1,
        place1: occupation?.place1,
        phone1: occupation?.phone1,
      },
      career2: {
        position2: occupation?.position2,
        org2: occupation?.org2,
        level2: occupation?.level2,
        place2: occupation?.place2,
        salary2: occupation?.salary2,
        phone2: occupation?.phone2,
      },
      career3: {
        position3: occupation?.position3,
        level3: occupation?.level3,
        org3: occupation?.org3,
        bussiness3: occupation?.bussiness3,
        place3: occupation?.place3,
        phone3: occupation?.phone3,
        salary3: occupation?.salary3,
      },
      career4: {
        firstName4: occupation?.firstName4,
        lastName4: occupation?.lastName4,
        place4: occupation?.place4,
        phone4: occupation?.phone4,
        salary4: occupation?.salary4,
      },
      career5: {
        type5: occupation?.type5,
        place5: occupation?.place5,
        phone5: occupation?.phone5,
        salary5: occupation?.salary5,
      },
      career6: {
        position6: occupation?.position6,
        level6: occupation?.level6,
        org6: occupation?.org6,
        bussiness6: occupation?.bussiness6,
        place6: occupation?.place6,
        phone6: occupation?.phone6,
        salary6: occupation?.salary6,
        other6: occupation?.other6,
      },
    };
    return { ...response, data };
  }
  return response;
}

async function getBusiness(
  request: GetBusinessRequest,
): Promise<GetBusinessResponse> {
  const pathname = registerPath.business;
  const response = await fetchSenateAPI({
    method,
    pathname,
    debug: "getBusiness",
    request,
  });
  if (response.status === 200 && !_.isEmpty(response.data)) {
    const rawData = response.data;
    const businesses = rawData?.businesses || [];
    const data: BusinessResponse = {
      status: rawData?.status,
      lastStep: rawData?.lastStep || 0,
      businesses: businesses.map((item: any) => ({
        id: item?.id,
        name: item?.name || "",
        place: item?.place || "",
        phone: item?.phone || "",
      })),
    };
    return { ...response, data };
  }
  return response;
}

async function getIncompatibility(
  request: GetIncompatibilityRequest,
): Promise<GetIncompatibilityResponse> {
  const pathname = registerPath.incompatibility;
  const response = await fetchSenateAPI({
    method,
    pathname,
    debug: "getIncompatibility",
    request,
  });
  if (response.status === 200 && !_.isEmpty(response.data)) {
    const rawData = response.data;
    const incompatibility = rawData?.incompatibility || {};
    const data: IncompatibilityB = {
      status: rawData?.status,
      lastStep: rawData?.lastStep || 0,

      "1-1": getNumberData(incompatibility["1-1"]),
      "1-2": getNumberData(incompatibility["1-2"]),
      "1-1Etc": incompatibility["1-1Etc"] || "",
      "1-2Etc": incompatibility["1-2Etc"] || "",

      "2-1": getNumberData(incompatibility["2-1"]),
      "2-1Etc": incompatibility["2-1Etc"] || "",

      "3-1": getNumberData(incompatibility["3-1"]),
      "3-2": getNumberData(incompatibility["3-2"]),
      "3-1Etc": incompatibility["3-1Etc"] || "",
      "3-2Etc": incompatibility["3-2Etc"] || "",

      "4-1": getNumberData(incompatibility["4-1"]),
      "4-1Etc": incompatibility["4-1Etc"] || "",

      "5-1": getNumberData(incompatibility["5-1"]),
      "5-2": getNumberData(incompatibility["5-2"]),
      "5-1Etc": incompatibility["5-1Etc"] || "",
      "5-2Etc": incompatibility["5-2Etc"] || "",

      "6-1": getNumberData(incompatibility["6-1"]),
      "6-1Etc": incompatibility["6-1Etc"] || "",

      "7-1": getNumberData(incompatibility["7-1"]),
      "7-1Etc": incompatibility["7-1Etc"] || "",

      "8-1": getNumberData(incompatibility["8-1"]),
      "8-1Etc": incompatibility["8-1Etc"] || "",

      "9-1": getNumberData(incompatibility["9-1"]),
      "9-1Etc": incompatibility["9-1Etc"] || "",

      "10-1": getNumberData(incompatibility["10-1"]),
      "10-1Etc": incompatibility["10-1Etc"] || "",

      "11-1": getNumberData(incompatibility["11-1"]),
      "11-1Etc": incompatibility["11-1Etc"] || "",

      "12-1": getNumberData(incompatibility["12-1"]),
      "12-2": getNumberData(incompatibility["12-2"]),
      "12-1Etc": incompatibility["12-1Etc"] || "",
      "12-2Etc": incompatibility["12-2Etc"] || "",

      "13-1": getNumberData(incompatibility["13-1"]),
      "13-1Etc": incompatibility["13-1Etc"] || "",

      "14-1": getNumberData(incompatibility["14-1"]),
      "14-1Etc": incompatibility["14-1Etc"] || "",

      "15-1": getNumberData(incompatibility["15-1"]),
      "15-1Etc": incompatibility["15-1Etc"] || "",

      "16-1": getNumberData(incompatibility["16-1"]),
      "16-1Etc": incompatibility["16-1Etc"] || "",

      "17-1": getNumberData(incompatibility["17-1"]),
      "17-1Etc": incompatibility["17-1Etc"] || "",

      "18-1": getNumberData(incompatibility["18-1"]),
      "18-2": getNumberData(incompatibility["18-2"]),
      "18-1Etc": incompatibility["18-1Etc"] || "",
      "18-2Etc": incompatibility["18-2Etc"] || "",

      "19-1": getNumberData(incompatibility["19-1"]),
      "19-2": getNumberData(incompatibility["19-2"]),
      "19-1Etc": incompatibility["19-1Etc"] || "",
      "19-2Etc": incompatibility["19-2Etc"] || "",

      "20-1": getNumberData(incompatibility["20-1"]),
      "20-1Etc": incompatibility["20-1Etc"] || "",

      "21-1": getNumberData(incompatibility["21-1"]),
      "21-1Etc": incompatibility["21-1Etc"] || "",
    };
    return { ...response, data };
  }
  return response;
}

async function getPolitical(
  request: GetPoliticalRequest,
): Promise<GetPoliticalResponse> {
  const pathname = registerPath.political;
  const response = await fetchSenateAPI({
    method,
    pathname,
    debug: "getPolitical",
    request,
  });
  if (response.status === 200 && !_.isEmpty(response.data)) {
    const rawData = response.data;
    const political = rawData?.political || {};
    const list = rawData?.politicalPartyList || [];

    const { m1, p1, m2, p2 } = groupByType(list);

    const listM1: TableFiveData[] = m1.length
      ? m1.map(mapTableFiveData)
      : [{ ...defaultTableFive }];
    const listP1: TableSixData[] = p1.length
      ? p1.map(mapTableSixData)
      : [{ ...defaultTableSix }];
    const listM2: TableFiveData[] = m2.length
      ? m2.map(mapTableFiveData)
      : [{ ...defaultTableFive }];
    const listP2: TableSixData[] = p2.length
      ? p2.map(mapTableSixData)
      : [{ ...defaultTableSix }];

    const data: PoliticalParty = {
      status: rawData?.status,
      lastStep: rawData?.lastStep || 0,
      section1: {
        radio1: getNumberData(political?.formerMember),
        radio2: getNumberData(political?.formerPosition),
        list1: listM1,
        list2: listP1,
      },
      section2: {
        radio1: getNumberData(political?.formerMember2),
        radio2: getNumberData(political?.formerPosition2),
        list1: listM2,
        list2: listP2,
      },
      section3: {
        radio1: getNumberData(political?.relationStatus),
        detail1: political?.politicalRelation || "",
      },
    };
    return { ...response, data };
  }
  return response;
}

async function getDiscipline(
  request: GetDisciplineRequest,
): Promise<GetDisciplineResponse> {
  const pathname = registerPath.discipline;
  const response = await fetchSenateAPI({
    method,
    pathname,
    request,
    debug: "getDiscipline",
  });
  if (response.status === 200 && !_.isEmpty(response.data)) {
    const rawData = response.data;
    const disciplines = rawData?.disciplines || [];
    const complains: ComplainB[] = disciplines
      .filter((item: any) => item?.disciplineType === "C")
      .map((item: any) => ({
        id: item?.id,
        disciplineOrg: item?.disciplineOrg || "",
        disciplineDate: item?.disciplineDate
          ? dayjs(item.disciplineDate)
          : undefined,
        disciplineName: item?.disciplineName || "",
        disciplineDescription: item?.disciplineDescription || "",
        disciplineResult: item?.disciplineResult || "",
      }));
    const proceeds: ProceedB[] = disciplines
      .filter((item: any) => item?.disciplineType === "P")
      .map((item: any) => ({
        id: item?.id,
        disciplineStatus: getNumberData(item?.disciplineStatus),
        disciplineOrg: item?.disciplineOrg || "",
        disciplineDate: item?.disciplineDate
          ? dayjs(item.disciplineDate)
          : undefined,
        disciplineName: item?.disciplineName || "",
        disciplineDescription: item?.disciplineDescription || "",
        disciplineResult: item?.disciplineResult || "",
      }));
    const data: HistoryOfWrongdoing = {
      status: rawData?.status,
      lastStep: rawData?.lastStep || 0,
      radio1: undefined,
      radio2: undefined,
      deleteDisciplines: [],
      complains,
      proceeds,
    };
    return { ...response, data };
  }
  return response;
}

async function getKnowledge(
  request: GetKnowledgeRequest,
): Promise<GetKnowledgeResponse> {
  const pathname = registerPath.knowledge;
  const response = await fetchSenateAPI({
    method,
    pathname,
    request,
    debug: "getKnowledge",
  });
  if (response.status === 200 && !_.isEmpty(response.data)) {
    const rawData = response.data;
    const data: AcademicKnowledge = {
      status: rawData?.status,
      lastStep: rawData?.lastStep || 0,
      knowledge: rawData?.knowledge || "",
    };
    return { ...response, data };
  }
  return response;
}

async function getConcept(
  request: GetConceptRequest,
): Promise<GetConceptResponse> {
  const pathname = registerPath.concept;
  const response = await fetchSenateAPI({
    method,
    pathname,
    request,
    debug: "getConcept",
  });
  if (response.status === 200 && !_.isEmpty(response.data)) {
    const rawData = response.data;
    const data: Concept = {
      status: rawData?.status,
      lastStep: rawData?.lastStep || 0,
      concept: rawData?.concept || "",
    };
    return { ...response, data };
  }
  return response;
}

async function getAffiliation(
  request: GetAffiliationRequest,
): Promise<GetAffiliationResponse> {
  const pathname = registerPath.affiliation;
  const response = await fetchSenateAPI({
    method,
    pathname,
    request,
    debug: "getAffiliation",
  });
  if (response.status === 200 && !_.isEmpty(response.data)) {
    const rawData = response.data;
    const affiliations = rawData?.affiliations || [];
    const data: FinalAffiliationB = {
      status: rawData?.status,
      lastStep: rawData?.lastStep || 0,
      deleteAffiliations: [],
      affiliations: affiliations.map((item: any) => ({
        id: item?.id,
        to: item?.to || "",
        name: item?.name || "",
        place: item?.place || "",
        alley: item?.alley || "",
        road: item?.road || "",
        phone: item?.phone || "",
        position: item?.position || "",
        provinceId: item?.provinceId,
        amphurId: item?.amphurId,
        tumbolId: item?.districtId,
        zipCodeId: item?.zipCodeId,
        isPresent: item?.isPresent === 1 ? true : false,
        startDate: item?.startDate ? dayjs(item.startDate) : undefined,
        endDate: item?.endDate ? dayjs(item.endDate) : undefined,
      })),
    };
    return { ...response, data };
  }
  return response;
}

async function getUploadFile(
  request: GetUploadFileRequest,
): Promise<GetUploadFileResponse> {
  const pathname = registerPath.uploadFile;
  const response = await fetchSenateAPI({
    method,
    pathname,
    request,
    debug: "getUploadFile",
  });
  if (response.status === 200 && !_.isEmpty(response.data)) {
    const rawData = response.data;
    const files = rawData?.files || [];
    const data: UploadDocumentsB = {
      status: rawData?.status,
      lastStep: rawData?.lastStep || 0,
      registerType: rawData?.registerType,
      activeProperties: rawData?.activeProperties || [],
      deleteFiles: [],
      files: files.map((item: any) => ({
        id: item?.id,
        keyId: item?.keyId,
        name: item?.originalName || "",
        type: item?.type || "",
        file: "",
        group: item?.type || "",
        path: item?.path || "",
      })),
      isError: {},
      thaiParents: rawData?.thaiParents,
      everChangedName: rawData?.everChangedName,
      incompatibility: rawData?.incompatibility || {},
      formerPolitician: rawData?.formerPolitician || false,
      prosecute: rawData?.prosecute || false,
    };
    return { ...response, data };
  }
  return response;
}

async function getKnowledgeAndAbility(
  request: GetKnowledgeAndAbilityRequest,
): Promise<GetKnowledgeAndAbilityResponse> {
  const pathname = registerPath.knowledgeAndAbility;
  const response = await fetchSenateAPI({
    method,
    pathname,
    request,
    debug: "getKnowledgeAndAbility",
  });
  if (response.status === 200 && !_.isEmpty(response.data)) {
    const rawData = response.data;
    const data: KnowledgeAndAbility = {
      status: rawData?.status,
      lastStep: rawData?.lastStep || 0,
      deleteKAAs: [],
      KAAs: rawData?.KAAs || [],
    };
    return { ...response, data };
  }
  return response;
}

async function getExpertiseOrExperience(
  request: GetExpertiseOrExperienceRequest,
): Promise<GetExpertiseOrExperienceResponse> {
  const pathname = registerPath.expertiseOrExperience;
  const response = await fetchSenateAPI({
    method,
    pathname,
    request,
    debug: "getExpertiseOrExperience",
  });
  if (response.status === 200 && !_.isEmpty(response.data)) {
    const rawData = response.data;
    const EOEs = rawData?.EOEs ? mapExperience(rawData.EOEs) : [];
    const data: ExpertiseOrExperience = {
      status: rawData?.status,
      lastStep: rawData?.lastStep || 0,
      deleteEOEs: [],
      EOEs,
    };
    return { ...response, data };
  }
  return response;
}

async function getCandidate(
  request: GetCandidateRequest,
): Promise<GetCandidateResponse> {
  const pathname = registerPath.candidate;
  const response = await fetchSenateAPI({
    method,
    pathname,
    request,
    debug: "getCandidate",
  });
  if (response.status === 200 && !_.isEmpty(response.data)) {
    const rawData = response.data;
    const status = rawData?.status;
    const data: FinalStageResponse = {
      status,
      lastStep: rawData?.lastStep || 0,
      updatedDate: rawData?.updatedDate,
      submitDate: rawData?.submitDate,
      startDate: rawData?.startDate,
      endDate: rawData?.endDate,
      info: {
        prefix: rawData?.prefix || "",
        firstName: rawData?.firstName || "",
        lastName: rawData?.lastName || "",
        identityCard: rawData?.identityCard || "",
        nameOfPositionAppliedFor: rawData?.nameOfPositionAppliedFor || "",
      },
    };
    return { ...response, data };
  }
  return response;
}

async function getLastStep(
  request: GetLastStepRequest,
): Promise<GetLastStepResponse> {
  const pathname = registerPath.lastStep;
  const response = await fetchSenateAPIWithLocalStorage({
    method,
    pathname,
    request,
    debug: "getLastStep",
    key: localStorageKey.REGISTER_LAST_STEP,
    expiry: 0.1, // 6 seconds
  });
  if (response.status === 200 && !_.isEmpty(response.data)) {
    const rawData = response.data;
    const data: LastStepResponse = {
      id: rawData?.id,
      status: rawData?.status || "D",
      step: rawData?.lastStep || 0,
    };
    return { ...response, data };
  }
  return response;
}

async function getListHistory(): Promise<GetListHistoryResponse> {
  const pathname = registerPath.listHistory;
  const response = await fetchSenateAPI({
    method,
    pathname,
    debug: "getListHistory",
    auth: false,
  });
  if (response.status === 200 && !_.isEmpty(response.data)) {
    const data = response.data.map((item: RegisterHistory) => ({
      ...item,
      type: item?.type ? item.type.toLowerCase() : "",
      createdDate: item?.createdDate
        ? dayjs(item.createdDate).format("D MMMM BBBB")
        : "-",
      updatedDate: item?.updatedDate
        ? dayjs(item.updatedDate).format("D MMMM BBBB")
        : "-",
      submitDate: item?.submitDate
        ? dayjs(item.submitDate).format("D MMMM BBBB")
        : "-",
      startDate: item?.startDate ? dayjs(item.startDate) : undefined,
      endDate: item?.endDate ? dayjs(item.endDate) : undefined,
      registerNo: item?.registerNo,
    }));
    return { ...response, data };
  }
  return response;
}

async function getFileAnnounceType(
  request: GetFileAnnounceTypeRequest,
): Promise<GetFileAnnounceTypeResponse> {
  const pathname = registerPath.fileAnnounceType;
  const response = await fetchSenateAPI({
    method,
    pathname,
    request,
    debug: "getFileAnnounceType",
    auth: false,
  });
  if (response.status === 200 && !_.isEmpty(response.data)) {
    const data = response.data.length
      ? response.data.reduce((acc: any, file: FileAnnounce) => {
          const index = acc.findIndex(
            (item: any) => item.typeNameCode === file.typeNameCode,
          );
          if (index > -1) {
            acc[index].files.push(file);
          } else {
            acc.push({
              typeNameCode: file.typeNameCode,
              typeName: file.typeName,
              files: [file],
            });
          }
          return acc;
        }, [])
      : [];
    return { ...response, data };
  }
  return response;
}

async function getPrefix(
  request: GetPrefixRequest,
): Promise<GetPrefixResponse> {
  const pathname = registerPath.prefixName;
  const response = await fetchSenateAPI({
    method,
    pathname,
    request,
    debug: "getPrefix",
    auth: false,
  });
  if (
    response.status === 200 &&
    !_.isEmpty(response.data) &&
    response.data?.list
  ) {
    const data: Prefix[] = response.data.list.map(
      ({
        id,
        title_code,
        title_name,
        title_shortname,
        title_name_en,
      }: any) => ({
        id,
        titleCode: title_code,
        value: title_name,
        label: title_name,
        titleShortname: title_shortname,
        titleNameEn: title_name_en,
      }),
    );
    return { ...response, data };
  }
  return response;
}

async function getEducationList(id: number): Promise<GetEducationListResponse> {
  const pathname = `${registerPath.educationList}/${id}`;
  const response = await fetchSenateAPI({
    method,
    pathname,
    debug: "getEducationList",
    auth: false,
  });
  if (
    response.status === 200 &&
    !_.isEmpty(response.data) &&
    response.data?.eduList
  ) {
    const data = response.data.eduList.map(({ id, name }: any, i: number) => ({
      id,
      key: `education-option-${i}`,
      value: name,
      label: name,
    }));
    return { ...response, data };
  }
  return response;
}

async function getNationality(): Promise<GetNationalityResponse> {
  const pathname = registerPath.nationality;
  const response = await fetchSenateAPIWithLocalStorage({
    method,
    pathname,
    debug: "getNationality",
    auth: false,
    key: localStorageKey.REGISTER_NATIONALITY,
  });
  if (
    response.status === 200 &&
    !_.isEmpty(response.data) &&
    response.data?.nationality
  ) {
    const data = response.data.nationality.map(
      ({ id, name }: any, i: number) => ({
        id,
        key: `nationality-option-${i}`,
        value: name,
        label: name,
      }),
    );
    return { ...response, data };
  }
  return response;
}

async function getUserProfile(): Promise<GetUserProfileResponse> {
  const pathname = registerPath.userProfile;
  const response = await fetchSenateAPI({
    method,
    pathname,
    debug: "getUserProfile",
    auth: true,
  });
  const { status, data: rawData } = response;
  if (status === 200 && !_.isEmpty(rawData)) {
    const data = {
      title: rawData?.title,
      prefix: rawData?.prefix,
      firstName: rawData?.firstName,
      lastName: rawData?.lastName,
      identityCard: rawData?.identityCard,
      birthDate: rawData?.birthDate ? dayjs(rawData.birthDate) : undefined,
      email: rawData?.email,
    };
    return { ...response, data };
  }
  return response;
}

async function getUserInformation(
  request: GetUserInformationRequest,
): Promise<GetUserInformationResponse> {
  const pathname = registerPath.userInformation;
  const response = await fetchSenateAPI({
    method,
    pathname,
    debug: "getUserInformation",
    auth: true,
    request,
  });
  return response;
}

async function getListForCommittee(): Promise<GetListForCommitteeResponse> {
  const pathname = registerPath.listForCommittee;
  const response = await fetchSenateAPIWithLocalStorage({
    method,
    pathname,
    debug: "getListForCommittee",
    auth: true,
    key: localStorageKey.REGISTER_LIST_FOR_COMMITTEE,
    expiry: 5,
  });
  return response;
}

async function getListCandidateInfoById(
  id: number,
): Promise<GetListCandidateInfoByIdResponse> {
  const pathname = `${registerPath.listCandidateInfoById}/${id}`;
  const response = await fetchSenateAPI({
    method,
    pathname,
    debug: "getListCandidateInfoById",
    auth: true,
  });
  return response;
}

async function getReplacement(id: number): Promise<GetReplacementResponse> {
  const pathname = `${registerPath.replacement}/${id}`;
  const response = await fetchSenateAPI({
    method,
    pathname,
    debug: "getReplacement",
    auth: true,
  });
  return response;
}

async function getFormConfig(id: number): Promise<GetFormConfigResponse> {
  const pathname = `${registerPath.formConfig}/${id}`;
  const response = await fetchSenateAPI({
    method,
    pathname,
    debug: "getFormConfig",
    auth: true,
  });
  return response;
}

async function getPropertiesUploadFile(
  senateRecruitDetailId: string,
  userId: string,
  orgId: string,
): Promise<GetPropertiesUploadFileResponse> {
  const pathname = `${registerPath.propertiesUploadFile}/${senateRecruitDetailId}/${userId}/${orgId}`;
  const response = await fetchSenateAPI({
    method,
    pathname,
    debug: "getPropertiesUploadFile",
    auth: true,
  });
  return response;
}

async function getConsentsBySRDId(
  senateRecruitDetailId?: string | number,
): Promise<GetConsentsBySRDIdResponse> {
  const pathname = `${registerPath.consentsBySRDId}/${senateRecruitDetailId}`;
  const response = await fetchSenateAPIWithLocalStorage({
    method,
    pathname,
    debug: "getConsentsBySRDId",
    auth: true,
    key: localStorageKey.REGISTER_CONSENTS,
    expiry: 0.05,
  });
  return response;
}

async function getAgeText(
  request: GetAgeTextRequest,
): Promise<GetAgeTextResponse> {
  const pathname = registerPath.getAgeText;
  const response = await fetchSenateAPI({
    method,
    pathname,
    debug: "getAgeText",
    auth: true,
    request,
  });
  if (response.status === 200 && !_.isEmpty(response.data)) {
    const rawData = response.data;
    
    const data : AgeTextRR = {
      ageText: rawData
    };
    return { ...response, data };
  }
  return response;
}

async function getSumDateText(
  request: GetSumDateTextRequest,
): Promise<GetSumDateTextResponse> {
  const pathname = registerPath.getSumDateText;
  const response = await fetchSenateAPI({
    method,
    pathname,
    debug: "getSumDateText",
    auth: true,
    request,
  });
  if (response.status === 200 && !_.isEmpty(response.data)) {
    const rawData = response.data;
    
    const data : SumTextRR = {
      sumText: rawData
    };
    return { ...response, data };
  }
  return response;
}

export {
  getAddress,
  getAffiliation,
  getAumphur,
  getAumphur2,
  getBusiness,
  getCandidate,
  getConcept,
  getConsent,
  getConsentsBySRDId,
  getDiscipline,
  getEducation,
  getEducationList,
  getExperience,
  getExpertiseOrExperience,
  getFileAnnounceType,
  getIncompatibility,
  getKnowledge,
  getKnowledgeAndAbility,
  getLastStep,
  getList,
  getListCandidateInfoById,
  getListForCommittee,
  getListHistory,
  getNationality,
  getOccupation,
  getPersonalRecord,
  getPolitical,
  getPrefix,
  getProperty,
  getProvince,
  getProvince2,
  getReplacement,
  getTitleList,
  getTumbol,
  getTumbol2,
  getUploadFile,
  getUserInformation,
  getUserProfile,
  getZipCode,
  getFormConfig,
  getPropertiesUploadFile,
  getAgeText,
  getSumDateText,
};
